"use client"

import { applicantsByProfileId, applicantsHaveMatchScores } from '@yeshuman/sdk'
import type { UnifiedCandidate } from '@yeshuman/sdk'
import { useMemo } from 'react'

import type { ApplicantCard, ApplicantProfileSummary } from '../../hooks/use-opportunity-applicants'
import type { JobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'
import type { JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import { SmartAnalysisScoreboard } from './smart-analysis-scoreboard'
import { SmartAnalysisFindings } from './smart-analysis-findings'
import { SmartAnalysisLeaderboard } from './smart-analysis-leaderboard'
import { SmartAnalysisBeyondPipeline } from './smart-analysis-beyond-pipeline'
import type { OpenJobWorkspacePersonHandler } from '../../lib/job-workspace-person-paths'

export type SmartAnalysisJobReportProps = {
  applicants: ApplicantCard[]
  candidates: UnifiedCandidate[]
  prospectMatches: JobProspectMatch[]
  coverage?: JobAnalysisCoverage | null
  applicantProfileById?: Record<string, ApplicantProfileSummary>
  searchSummary?: string | null
  llmJudgedCount?: number | null
  onReviewPipeline: () => void
  onReviewInvite: () => void
  onRunAnalysis?: () => void
  onViewInPipeline: (profileId: string) => void
  onOpenPerson?: OpenJobWorkspacePersonHandler
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
  readOnly?: boolean
  previousRunAt?: string | null
  className?: string
}

export function SmartAnalysisJobReport({
  applicants,
  candidates,
  prospectMatches,
  coverage = null,
  applicantProfileById,
  searchSummary,
  llmJudgedCount,
  onReviewPipeline,
  onReviewInvite,
  onRunAnalysis,
  onViewInPipeline,
  onOpenPerson,
  onNudge,
  nudgePending,
  readOnly = false,
  previousRunAt,
  className,
}: SmartAnalysisJobReportProps) {
  const applicantByProfileId = useMemo(() => applicantsByProfileId(applicants), [applicants])
  // The scoreboard reads band counts off the pipeline board, so it stays hidden until
  // board scores land (zero tiles read as a failed run). The leaderboard reads the run
  // results directly and can lead while the board refetches after a live run.
  const hasPipelineScores = applicantsHaveMatchScores(applicants)
  const hasRankedCandidates = candidates.length > 0

  return (
    <div className={className ?? 'space-y-6'}>
      {hasPipelineScores ? (
        <SmartAnalysisScoreboard
          applicants={applicants}
          coverage={coverage}
          summary={searchSummary}
          llmJudgedCount={llmJudgedCount}
        />
      ) : null}
      <SmartAnalysisFindings
        applicants={applicants}
        previousRunAt={previousRunAt}
        onReviewPipeline={onReviewPipeline}
        onRunAnalysis={onRunAnalysis}
        onNudge={readOnly ? undefined : onNudge}
        nudgePending={nudgePending}
        readOnly={readOnly}
      />
      {hasRankedCandidates ? (
        <SmartAnalysisLeaderboard
          variant="job-report"
          candidates={candidates}
          applicantProfileById={applicantProfileById}
          applicantByProfileId={applicantByProfileId}
          onViewInPipeline={readOnly ? undefined : onViewInPipeline}
          onOpenPerson={onOpenPerson}
          onNudge={readOnly ? undefined : onNudge}
          nudgePending={nudgePending}
          readOnly={readOnly}
        />
      ) : null}
      {hasPipelineScores || hasRankedCandidates ? (
        <SmartAnalysisBeyondPipeline
          matches={prospectMatches}
          onReviewInvite={onReviewInvite}
          onOpenPerson={onOpenPerson}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  )
}
